import MapComponent from "./MapComponent";
import "./App.css";
import { Avatar, Button, Dropdown, Layout, List, Select, Tooltip } from "antd";
import { displayPrice, displayNumber } from './utils/formatters';
import Sider from "antd/es/layout/Sider";
import { Content, Header } from "antd/es/layout/layout";
import { FC, useEffect, useState } from "react";
import { debounce } from "lodash";
import TransactionsFaoFilters, {
  getDefaultTransactionsFaoFilters,
} from "./filters/TransactionsFaoFilters";
import BiensOffMarketFilters from "./filters/BiensOffMarketFilters";
import Link from "antd/es/typography/Link";
import {
  CheckSquareFilled,
  UserOutlined,
  BorderOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import axiosInstance from "./axiosIntance";
import { useNavigate, useLoaderData } from "react-router-dom";

const elementInViewport = (el: any) => {
  var top = el.offsetTop;
  var left = el.offsetLeft;
  var width = el.offsetWidth;
  var height = el.offsetHeight;

  while (el.offsetParent) {
    el = el.offsetParent;
    top += el.offsetTop;
    left += el.offsetLeft;
  }

  return (
    top >= window.pageYOffset &&
    left >= window.pageXOffset &&
    top + height <= window.pageYOffset + window.innerHeight &&
    left + width <= window.pageXOffset + window.innerWidth
  );
};

// Moved to ./utils/formatters.ts
export const displayStatus = (value: string) => (
  <div
    style={{
      width: 12,
      minWidth: 12,
      height: 12,
      minHeight: 12,
      borderRadius: 12,
      backgroundColor: value ? "green" : "red",
      marginTop: 4,
    }}
  ></div>
);

const prices = ["Prix", "Prix_m2_habitable", "Prix_m2_terrain"];
const numbers = [
  "SVB_Verifiee",
  "SVB_Estimee",
  "AnneeConstruction",
  "Etage",
  "Pieces",
];

const defaultFields = [
  "Prix",
  "Prix_m2_habitable",
  "Pieces",
  "SVB_Verifiee",
  "SVB_Estimee",
  "AnneeConstruction",
  "Etage",
  "Prix_m2_terrain",
];

const sidebarInfo: Record<string, string[]> = {
  Appartement: [
    "Prix",
    "Prix_m2_habitable",
    "Pieces",
    "SVB_Verifiee",
    "SVB_Estimee",
    "AnneeConstruction",
    "Etage",
  ],
  "Habitation à un seul logement": [
    "Prix",
    "Prix_m2_habitable",
    "Pieces",
    "SVB_Verifiee",
    "SVB_Estimee",
    "AnneeConstruction",
    "Prix_m2_terrain",
  ],
  Terrain: ["Prix", "Prix_m2_terrain"],
  Garage: ["Prix", "AnneeConstruction"],
  Parking: ["Prix"],
};

const fieldLabels: Record<string, string> = {
  Prix: "Prix",
  Prix_m2_habitable: "Prix/m2 habitable",
  Pieces: "Nombre de pièces",
  SVB_Verifiee: "SVB Verifiée",
  SVB_Estimee: "SVB Estimée",
  AnneeConstruction: "Année de construction",
  Prix_m2_terrain: "M2 terrain",
  Etage: "Etage",
};

function App() {
  const navigate = useNavigate();
  const [parcelles, setParcelles] = useState<any[]>([]);
  const [selectedParcelle, setSelectedParcelle] = useState<string | null>(null);
  const [address, setAddress] = useState("");

  const [searchResult, setSearchResult] = useState<any[]>([]);
  const [center, setCenter] = useState("6.1432|46.2044");
  const [marker, setMarker] = useState("|");
  const [filters, setFilters] = useState(getDefaultTransactionsFaoFilters);
  const [biensOffMarketFilters, setBiensOffMarketFilters] = useState({});
  const [checkedTransactions, setCheckedTransactions] = useState<any[]>([]);
  const [filterSidebarVisible, setFilterSidebarVisible] = useState(true);
  const [transactionsSidebarVisible, setTransactionsSidebarVisible] = useState(true);
  const [showTransactionsFao, setShowTransactionsFao] = useState(true);
  const [showBiensOffMarket, setShowBiensOffMarket] = useState(false);
  const { user } = useLoaderData() as {
    user: any;
  };

  useEffect(() => {
    selectedParcelle && scrollToItem(selectedParcelle);
  }, [parcelles, selectedParcelle]);

  useEffect(() => {
    const searchAddress = async (address: string) => {
      const response = await axiosInstance.get(
        `https://melcarnebackend.digipulse.ch/geocoder?s=${address}`
      );

      setSearchResult(response.data);
    };

    if (address) searchAddress(address);
  }, [address]);

  const scrollToItem = (itemId: string) => {
    const element = document.getElementById(itemId);

    if (element && !elementInViewport(element)) {
      element.scrollIntoView({ behavior: "auto" });
    }
  };

  const debounceOnChange = debounce(setAddress, 1000);

  const handleAddressChanged = (placeId: string) => {
    if (!placeId) {
      setAddress("");
      return;
    }

    const place = searchResult.find((r) => r.place_id === placeId)!;
    setCenter(`${place.lon}|${place.lat}`);
    setAddress(place.label);
    
    // For neighborhoods, pass the full place object; for regular addresses, just coordinates
    if (place.type === 'neighborhood' && place.polygon) {
      setMarker(JSON.stringify(place));
    } else {
      setMarker(`${place.lon}|${place.lat}`);
    }
  };

  return (
    <Layout style={{ height: "100vh" }}>
      <Header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Link style={{ display: "flex", alignItems: "center" }} href={"/"}>
          <img height={54} src="/logo.svg" alt="Melcarne" />
        </Link>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Tooltip title={transactionsSidebarVisible ? "Masquer les transactions" : "Afficher les transactions"}>
            <Button
              type="text"
              icon={transactionsSidebarVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              onClick={() => setTransactionsSidebarVisible(!transactionsSidebarVisible)}
            />
          </Tooltip>
          <Select
            showSearch
            style={{ width: 400 }}
            placeholder="Entrer une adresse"
            defaultActiveFirstOption={false}
            suffixIcon={null}
            filterOption={false}
            onSearch={debounceOnChange}
            onChange={handleAddressChanged}
            notFoundContent={null}
            options={searchResult.map((result: any) => ({
              label: result.display_name,
              value: result.place_id,
            }))}
            allowClear
            value={address}
          />
          <Tooltip title={filterSidebarVisible ? "Masquer les filtres" : "Afficher les filtres"}>
            <Button
              type="text"
              icon={filterSidebarVisible ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setFilterSidebarVisible(!filterSidebarVisible)}
            />
          </Tooltip>
        </div>

        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: 1, label: `${user.first_name} ${user.last_name}` },
              {
                key: 2,
                label: (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      window.open("https://melcarne.digipulse.ch/change_password", "_blank");
                    }}
                  >
                    Changer de mot de passe
                  </Button>
                ),
              },
              {
                key: 3,
                label: (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      localStorage.removeItem("x-token");
                      navigate("/login");
                    }}
                  >
                    Se déconnecter
                  </Button>
                ),
              },
            ],
          }}
          placement="bottomRight"
        >
          <Avatar
            style={{ cursor: "pointer" }}
            size="large"
            src={`https://melcarnedirectus2.digipulse.ch/assets/${
              user.avatar
            }?key=system-medium-cover&access_token=${
              JSON.parse(localStorage.getItem("x-token")!).access_token
            }`}
            icon={<UserOutlined />}
          />
        </Dropdown>
      </Header>
      <Layout>
        {transactionsSidebarVisible && showTransactionsFao && (
          <Sider style={{ backgroundColor: "white" }} width={350}>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              borderBottom: "1px solid #f5f5f5",
              padding: 8,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: "bolder",
                color: "#757575",
                letterSpacing: 2,
                marginBottom: 12,
              }}
            >{`${parcelles.length} transaction${
              parcelles.length > 1 ? "s" : ""
            }`}</span>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                disabled={checkedTransactions.length === 0}
                size="small"
                onClick={async () => {
                  window.open(
                    `https://melcarnebackend.digipulse.ch/generate_export_pdf?transactionsIds=` +
                      JSON.stringify(checkedTransactions)
                  );
                }}
              >
                Générer PDF
              </Button>
              <Button
                disabled={checkedTransactions.length === 0}
                size="small"
                onClick={() => setCheckedTransactions([])}
              >
                Désélectionner
              </Button>
            </div>
          </div>
          <List style={{ height: "calc(100% - 64px)", overflow: "auto" }}>
            {parcelles.map((parcelle, index) => {
              const fields = sidebarInfo[parcelle["Usages"]] || defaultFields;
              const noParcelle = parcelle.NoParcelle;
              return (
                <List.Item
                  id={noParcelle}
                  key={index}
                  style={{
                    borderRight:
                      noParcelle === selectedParcelle
                        ? "3px solid #001482"
                        : "none",
                    backgroundColor:
                      noParcelle === selectedParcelle ? "#eeeeee" : "#ffffff",
                    cursor: "pointer",
                  }}
                  className="parcelle-item"
                  onClick={() => {
                    /*setCenter(
                      `${parcelle["position_sur_map"].coordinates[0]}|${parcelle["position_sur_map"].coordinates[1]}`
                    );*/
                    setSelectedParcelle(noParcelle);
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingLeft: 8,
                      paddingRight: 8,
                      width: "100%",
                      fontSize: 12,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: "bolder",
                        fontSize: 13,
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          textTransform: "uppercase",
                          gap: 8,
                        }}
                      >
                        {displayStatus(parcelle.SVB_Verifiee)}
                        <span>{`${parcelle.Adresse}, ${parcelle.NPA} ${parcelle.CommuneAdresse}`}</span>
                      </span>
                      <span style={{ minWidth: 70 }}>
                        {parcelle.Date_Transaction}
                      </span>
                    </div>
                    <div
                      style={{ marginLeft: 20, marginBottom: 4, fontSize: 11 }}
                    >
                      {parcelle.Usages}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      {fields
                        .map((f) => {
                          const value = parcelle[f];
                          if (numbers.includes(f)) {
                            return {
                              label: fieldLabels[f],
                              value: displayNumber(value),
                            };
                          }
                          if (prices.includes(f)) {
                            return {
                              label: fieldLabels[f],
                              value: displayPrice(value),
                            };
                          }

                          return { label: fieldLabels[f], value };
                        })
                        .filter((d: any) => !!d.value)
                        .map((data: any, index) => (
                          <DisplayField
                            key={index}
                            label={data.label}
                            value={data.value}
                          />
                        ))}
                    </div>
                    <div
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!parcelle.SVB_Verifiee) return;

                        if (checkedTransactions.includes(parcelle.id))
                          setCheckedTransactions(
                            checkedTransactions.filter((t) => t !== parcelle.id)
                          );
                        else
                          setCheckedTransactions([
                            ...checkedTransactions,
                            parcelle.id,
                          ]);
                      }}
                      style={{
                        position: "absolute",
                        bottom: 0,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      {parcelle.SVB_Verifiee ? (
                        <span>
                          {checkedTransactions.includes(parcelle.id) ? (
                            <CheckSquareFilled style={{ fontSize: 18 }} />
                          ) : (
                            <BorderOutlined style={{ fontSize: 18 }} />
                          )}
                        </span>
                      ) : (
                        <div style={{ width: 18 }} />
                      )}

                      <a
                        onClick={(event) => {
                          event.stopPropagation();
                          window.open(
                            `https://melcarne.digipulse.ch/collections/transactions_fao?id=${parcelle.id}`,
                            "_blank"
                          );
                        }}
                        style={{ textDecoration: "underline" }}
                        href="#"
                      >
                        Voir les détails
                      </a>
                    </div>
                  </div>
                </List.Item>
              );
            })}
          </List>
        </Sider>
        )}
        <Content>
          <MapComponent
            center={center}
            marker={marker}
            onParcellesChanged={(parcelles) => setParcelles(parcelles)}
            onSelectParcelle={(parcelle: string | null) => {
              setSelectedParcelle(parcelle);
            }}
            parcelleDetails={selectedParcelle}
            filters={filters}
            biensOffMarketFilters={biensOffMarketFilters}
            filterSidebarVisible={filterSidebarVisible}
            transactionsSidebarVisible={transactionsSidebarVisible && showTransactionsFao}
            showTransactionsFao={showTransactionsFao}
            setShowTransactionsFao={setShowTransactionsFao}
            onShowBiensOffMarketChange={setShowBiensOffMarket}
            onShowTransactionsFaoChange={setShowTransactionsFao}
          />
        </Content>
        {filterSidebarVisible && (showTransactionsFao || showBiensOffMarket) && (
          <Sider
            style={{
              backgroundColor: "white",
              height: "calc(100vh - 64px)",
              overflow: "auto",
            }}
            width={350}
          >
            {showTransactionsFao && <TransactionsFaoFilters onApplyFilters={setFilters} />}
            {showBiensOffMarket && <BiensOffMarketFilters onApplyFilters={setBiensOffMarketFilters} />}
          </Sider>
        )}
      </Layout>
    </Layout>
  );
}

export default App;

const DisplayField: FC<{ label: string; value: any }> = ({ label, value }) => {
  return (
    <div
      style={{
        width: "32%",
        marginBottom: 4,
        textAlign: "end",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <span style={{ color: "#757575", fontSize: 9 }}>{label}</span>
      <span style={{ fontSize: 12 }}>{value}</span>
    </div>
  );
};
